import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { SwalToastService } from 'src/app/services/swal.service';
import { IAddVitalSign } from 'src/app/interfaces/VitalSign/IAddVitalSign';
import { VitalSignService } from 'src/app/services/vitalSign.service';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import {
  dateRangeValidator,
  nz_datetime_format,
} from 'src/date/date.configuration';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';

@Component({
  selector: 'app-vital-signs-add-edit',
  templateUrl: './vital-signs-add-edit.component.html',
  styleUrls: ['./vital-signs-add-edit.component.css'],
  imports: [
    NzModalModule,
    TranslateModule,
    FormsModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    NzSelectModule,
    CommonModule,
    NzButtonModule,
    NzTableModule,
    NzLayoutModule,
    NzIconModule,
    NzTooltipModule,
    NzDatePickerModule,
  ],
})
export class VitalSignsAddEditComponent {
  private destroy$ = new Subject<void>();

  isVisible = false;
  isLoading = false;
  isDropdownLoading = false;

  isEdit = false;
  editVitalSignId?: number;

  vitalSignSubmitted = false;

  datetime_format = nz_datetime_format;

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef; // Reference to the modal

  vitalSignForm: FormGroup = new FormGroup({});

  rv = {
    temperature: {
      min: 0,
      max: 50,
    },
    respiratoryRate: {
      min: 0,
      max: 100,
    },
    heartRate: {
      min: 0,
      max: 250,
    },
    systolic: {
      min: 0,
      max: 350,
    },
    diastolic: {
      min: 0,
      max: 250,
    },
    spo2: {
      min: 0,
      max: 100,
    },
    urineOutput: {
      min: 0,
      max: 5,
    },
  };

  constructor(
    private readonly vitalSignService: VitalSignService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  ngOnInit() {
    this.isLoading = true;
    this.isDropdownLoading = true;
    this.isEdit = false;

    this.vitalSignForm = this.formBuilder.group({
      onSetValue: [
        null,
        [Validators.required, dateRangeValidator(undefined, new Date())],
      ],
      temperatureValue: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(this.rv.temperature.min),
          Validators.max(this.rv.temperature.max),
        ]),
      ],
      respiratoryRateValue: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(this.rv.respiratoryRate.min),
          Validators.max(this.rv.respiratoryRate.max),
        ]),
      ],
      heartRateValue: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(this.rv.heartRate.min),
          Validators.max(this.rv.heartRate.max),
        ]),
      ],
      systolicValue: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(this.rv.systolic.min),
          Validators.max(this.rv.systolic.max),
        ]),
      ],
      diastolicValue: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(this.rv.diastolic.min),
          Validators.max(this.rv.diastolic.max),
        ]),
      ],
      urineOutputValue: [
        null,
        Validators.compose([
          Validators.min(this.rv.urineOutput.min),
          Validators.max(this.rv.urineOutput.max),
        ]),
      ],
      spo2Value: [
        null,
        Validators.compose([
          Validators.required,
          Validators.min(this.rv.spo2.min),
          Validators.max(this.rv.spo2.max),
        ]),
      ],
    });

    this.vitalSignService.vitalSignEditSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        // GET DATA FIELDS AND ASSIGN DEFAULT VALUES TO FORM
        if (data) {
          this.isEdit = true;
          this.editVitalSignId = data?.id;

          this.vitalSignForm.patchValue({
            onSetValue:
              data?.onSetDateTime && this.convertToLocale(data?.onSetDateTime),
            temperatureValue: data?.temperature,
            respiratoryRateValue: data?.respiratoryRate,
            heartRateValue: data?.heartRate,
            systolicValue: data?.systolicBloodPressure,
            diastolicValue: data?.diastolicBloodPressure,
            urineOutputValue: data?.urineOutput,
            spo2Value: data?.spO2,
          });
        } else {
          this.isEdit = false;
          this.editVitalSignId = undefined;

          this?.vitalSignForm.reset();

          this.vitalSignForm.patchValue({
            onSetValue: this.convertToLocale(),
          });
        }
      });

    this.vitalSignService.vitalSignModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        this.isVisible = showModal;
      });

    this.isLoading = false;
  }

  ngOnDestroy() {
    // Unsubscribe from observables
    this.destroy$.next();
    this.destroy$.complete();

    this?.modalRef.destroy();
  }

  handleCancel() {
    // this.vitalSignForm.reset();

    this.vitalSignService.vitalSignModalSub.next(false);

    this.vitalSignSubmitted = false;

    this.isLoading = false;

    this?.modalRef.destroy();
  }

  submitForm() {
    this.isLoading = true;
    this.vitalSignSubmitted = true;

    if (this.vitalSignForm.valid) {
      const vitalSignObj: IAddVitalSign = {};

      vitalSignObj.id = this.editVitalSignId;

      vitalSignObj.onSetDateTime = moment(
        this.vitalSignForm.value?.onSetValue
      ).format();
      vitalSignObj.temperature = this.vitalSignForm.value?.temperatureValue;
      vitalSignObj.respiratoryRate =
        this.vitalSignForm.value?.respiratoryRateValue;
      vitalSignObj.heartRate = this.vitalSignForm.value?.heartRateValue;
      vitalSignObj.systolicBloodPressure =
        this.vitalSignForm.value?.systolicValue;
      vitalSignObj.diastolicBloodPressure =
        this.vitalSignForm.value?.diastolicValue;
      vitalSignObj.urineOutput = this.vitalSignForm.value?.urineOutputValue;
      vitalSignObj.spO2 = this.vitalSignForm.value?.spo2Value;

      if (this.isEdit) {
        this.vitalSignService.editVitalSign(vitalSignObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Vital Signs edited successfully'
            );
            this.vitalSignService.getData();

            this.handleCancel();
          },

          error: (err) => {
            this.swalToastService.toastError('Unable to edit Vital Signs');
            this.handleCancel();
          },
        });
      } else {
        this.vitalSignService.addVitalSign(vitalSignObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Vital Signs added successfully'
            );
            this.vitalSignService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to add Vital Signs');
            this.handleCancel();
          },
        });
      }
    } else {
      this.isLoading = false;
    }
  }

  getCurrentDateTime() {
    return new Date().toISOString().slice(0, 16);
  }

  disableFutureDates = (current: Date): boolean => {
    return current && current.getTime() > new Date().getTime();
  };

  // CONVERT UTC TO LOCAL
  convertToLocale(date?: string) {
    const currentDateTime = date ? moment(date).format() : moment().format();

    const isoDate = currentDateTime.slice(0, 16);

    return isoDate;
  }

  getTooltip(field: any): string {
    const minLabel = this.translate.instant('Min');
    const maxLabel = this.translate.instant('Max');
    return `${minLabel}: ${field.min}, ${maxLabel}: ${field.max}`;
  }
}
